import { BookOpen, ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { Collapse } from '@/components/Collapse'
import { Button } from '@/components/ui/button'
import { supportArticleUrl } from '@/lib/siteHealth'
import { useAnalytics, useDocsBaseUrl } from '@/provider/useAuth'
import { formatActionType } from './SiteHealthFormat'
import { actionTypeIcon, EYEBROW_CLASS } from './SiteHealthStyles'
import type { FixGuidanceProps } from './siteHealthComponents.types'

export default function FixGuidance({ guidance, subject }: FixGuidanceProps) {
  const [open, setOpen] = useState(false)
  const analytics = useAnalytics()
  const docsBaseUrl = useDocsBaseUrl()
  const articleUrl = supportArticleUrl(docsBaseUrl, guidance.supportArticleSlug)
  const ActionIcon = actionTypeIcon(guidance.actionType)
  const steps = guidance.fixSteps ?? []

  function toggleGuidance() {
    setOpen((current) => {
      const next = !current

      if (next) analytics.track('site_health_guidance_opened', subject)

      return next
    })
  }

  return (
    <div>
      <Button
        type="button"
        variant={open ? 'secondary' : 'default'}
        size="sm"
        aria-expanded={open}
        onClick={toggleGuidance}
      >
        <ActionIcon className="size-4" aria-hidden="true" />
        {open ? 'Hide fix guidance' : 'How to fix'}
        <ChevronDown
          className={`size-4 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </Button>

      <Collapse open={open}>
        <div className="mt-3 space-y-4 rounded-xl bg-muted/40 p-4">
          <div>
            <p className={EYEBROW_CLASS}>
              {formatActionType(guidance.actionType)}
            </p>
            {steps.length > 0 ? (
              <ol className="mt-3 space-y-4">
                {steps.map((step, index) => (
                  <li
                    key={`${index + 1}-${step.title}`}
                    className="grid grid-cols-[auto_1fr] gap-3"
                  >
                    <span className="flex size-6 items-center justify-center rounded-full bg-background text-xs tabular-nums text-muted-foreground ring-1 ring-border">
                      {index + 1}
                    </span>
                    <div className="space-y-1.5">
                      <h3 className="font-medium leading-6">{step.title}</h3>
                      <p className="text-sm leading-6 text-muted-foreground">
                        {step.description}
                      </p>
                      {step.code && (
                        <pre className="overflow-x-auto rounded-lg bg-slate-950 p-3 font-mono text-xs leading-5 text-slate-100">
                          <code>{step.code}</code>
                        </pre>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">
                Structured fix steps are not available for this finding.
              </p>
            )}
          </div>

          {articleUrl && (
            <a
              href={articleUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() =>
                analytics.track('site_health_support_article_opened', {
                  ...subject,
                  supportArticleSlug: guidance.supportArticleSlug,
                })
              }
              className="inline-flex items-center gap-2 text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <BookOpen className="size-4" aria-hidden="true" />
              Read the full guide
            </a>
          )}
        </div>
      </Collapse>
    </div>
  )
}
